import React, { useState, useRef, useEffect } from 'react';
import { X, Send, MessageSquare, Minimize2, Maximize2, Paperclip, FileText, Image as ImageIcon, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { aiAPI } from '../lib/api';
import { toast } from '../lib/toast';
import ReactMarkdown from 'react-markdown';
import AdaliaLogo from './AdaliaLogo';

// Lazy : le Wingman tire three.js + @react-three (~150 Ko gzip). En le chargeant en
// chunk async, le bundle initial reste léger ; l'avatar apparaît dès le chunk prêt.
const MaitreMultilexWingman = React.lazy(() => import('./MaitreMultilexWingman'));

export default function AIWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const messagesEndRef = useRef(null);
  const [hasIntroduced, setHasIntroduced] = useState(false);
  const [wingmanState, setWingmanState] = useState('idle'); // 'idle' | 'listening' | 'speaking' | 'thinking'
  const [pendingDocs, setPendingDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Message de présentation initial
  useEffect(() => {
    if (open && !hasIntroduced && messages.length === 0) {
      setHasIntroduced(true);
      setMessages([{
        role: 'assistant',
        content: "Bonjour Maître.\n\nJe suis **Maître Adalia**, votre assistant juridique. Je pratique l'**OHADA** et le **droit guinéen** : je réponds à vos questions, j'analyse vos pièces, je consulte vos dossiers et je peux vous proposer des actions — créer une échéance, rédiger un courrier, saisir du temps. Si la réponse dépend d'un texte récent ou d'une jurisprudence, je la **vérifie en ligne**.\n\nJoignez un document avec le trombone si vous souhaitez que je l'examine.\n\nJe vous écoute."
      }]);
    }
  }, [open, hasIntroduced, messages.length]);

  // Upload des pièces : même flux que la page complète (l'API renvoie un document_id
  // que l'on joint ensuite au message).
  const handleFileUpload = async (files) => {
    const filesArr = Array.from(files || []);
    if (filesArr.length === 0) return;
    setUploading(true);
    const uploaded = [];
    for (const file of filesArr) {
      if (file.size > 25 * 1024 * 1024) {
        toast.error(`${file.name} est trop volumineux (25 Mo au maximum)`);
        continue;
      }
      try {
        const res = await aiAPI.uploadDocument(file, conversationId);
        uploaded.push(res.data);
      } catch (err) {
        toast.error(`Échec de l'envoi de ${file.name} : ${err.response?.data?.detail || err.message}`);
      }
    }
    setPendingDocs(prev => [...prev, ...uploaded]);
    setUploading(false);
  };

  const removePendingDoc = (id) => setPendingDocs(prev => prev.filter(d => d.document_id !== id));

  const handleSend = async () => {
    // On peut envoyer une pièce seule, sans texte : la question devient implicite.
    if ((!input.trim() && pendingDocs.length === 0) || loading) return;
    const userMsg = input.trim() || 'Analyse les documents joints et donne ton avis juridique.';
    setInput('');
    const docIds = pendingDocs.map(d => d.document_id);
    const docNames = pendingDocs.map(d => d.filename);
    setPendingDocs([]);
    setMessages(prev => [...prev, { role: 'user', content: userMsg, document_names: docNames }]);
    setLoading(true);
    setWingmanState('thinking');
    try {
      const res = await aiAPI.chat({
        message: userMsg,
        conversation_id: conversationId,
        document_ids: docIds,
      });
      setConversationId(res.data.conversation_id);
      setWingmanState('speaking');
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
      setTimeout(() => setWingmanState('idle'), 2000);
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'Erreur inconnue';
      const code = err.response?.status;
      let friendly;
      if (code === 401) {
        // L'intercepteur axios va rediriger automatiquement dans 1,2s
        friendly = 'Votre session a expiré. Redirection vers la page de connexion…';
      } else if (code === 504 || code === 502) {
        friendly = 'Maître Adalia prend plus de temps que prévu. Réessayez avec une question plus courte, ou passez par la page complète du chat.';
      } else if (code === 503) {
        friendly = 'Le service est temporairement indisponible. Réessayez dans un instant.';
      } else if (code === 413) {
        friendly = 'Document trop volumineux : 25 Mo au maximum.';
      } else {
        friendly = `Erreur : ${detail}. Réessayez, ou contactez l'administrateur si le problème persiste.`;
      }
      setMessages(prev => [...prev, { role: 'assistant', content: friendly }]);
      setWingmanState('idle');
    }
    setLoading(false);
  };

  return (
    <>
      {/* Wingman toujours visible (sauf quand le chat est ouvert en plein écran) - RESPONSIVE.
          Suspense fallback null : rien ne s'affiche tant que le chunk three.js charge. */}
      <React.Suspense fallback={null}>
        <div className="hidden md:block">
          <MaitreMultilexWingman
            state={loading ? 'thinking' : open ? 'listening' : wingmanState}
            onClick={() => {
              setOpen(true);
              setWingmanState('listening');
            }}
            minimized={false}
          />
        </div>

        {/* Wingman mobile (plus petit) */}
        <div className="md:hidden">
          <MaitreMultilexWingman
            state={loading ? 'thinking' : open ? 'listening' : wingmanState}
            onClick={() => {
              setOpen(true);
              setWingmanState('listening');
            }}
            minimized={true}
          />
        </div>
      </React.Suspense>

      {/* Chat Panel */}
      {open && (
        <div
          className="fixed inset-0 md:inset-auto md:bottom-8 md:right-8 z-[9995] flex flex-col md:w-[480px] md:h-[680px]"
          style={{
            background: 'var(--widget-bg)',
            border: '2px solid var(--widget-border)',
            borderRadius: '0',
            boxShadow: '0 18px 50px rgba(0,0,0,0.55)',
            backdropFilter: 'blur(20px)'
          }}
          data-testid="ai-widget-panel"
        >
          {/* Header fixe */}
          <div className="flex items-center justify-between px-4 md:px-5 py-3 md:py-4 border-b flex-shrink-0" style={{borderColor:'var(--border-color)'}}>
            <div className="flex items-center gap-2 md:gap-3">
              {/* Emblème Adalia (balance de la justice) */}
              <div className="w-9 h-9 md:w-11 md:h-11 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--widget-border)' }}>
                <AdaliaLogo size={20} variant="mark" />
              </div>
              <div>
                <div className="text-[16px] font-serif" style={{color:'var(--text-primary)'}}>Maître Adalia</div>
                <div className="text-[11px] text-[#22C55E] flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#22C55E]"></span> Assistant juridique en ligne
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => { setOpen(false); navigate('/chat'); }} className="p-2 rounded-lg hover:bg-[var(--border-color)] transition-colors" data-testid="ai-widget-expand-button" title="Ouvrir la page complète">
                <Maximize2 className="w-4 h-4" style={{color:'var(--text-tertiary)'}} />
              </button>
              <button onClick={() => setOpen(false)} className="p-2 rounded-lg hover:bg-[var(--border-color)] transition-colors" data-testid="ai-widget-close-button" title="Réduire">
                <Minimize2 className="w-5 h-5" style={{color:'var(--text-tertiary)'}} />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.length === 0 && (
              <div className="text-center py-8">
                <div className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center" style={{background:'rgba(201,162,39,0.15)'}}>
                  <MessageSquare className="w-6 h-6 text-[#C9A227]" />
                </div>
                <div className="text-[13px] mb-1" style={{color:'var(--text-secondary)'}}>Comment puis-je vous aider ?</div>
                <div className="text-[11px]" style={{color:'var(--text-tertiary)'}}>Posez une question juridique</div>
              </div>
            )}
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div
                  className="max-w-[85%] rounded-[12px] px-3 py-2 text-[13px]"
                  style={msg.role === 'user'
                    ? { background: 'rgba(201,162,39,0.15)', border: '1px solid rgba(201,162,39,0.25)' }
                    : { background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)' }
                  }
                >
                  {msg.document_names?.length > 0 && (
                    <div className="mb-1.5 flex flex-wrap gap-1">
                      {msg.document_names.map((n, k) => (
                        <span key={k} className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded" style={{background:'var(--bg-tertiary)', color:'var(--text-tertiary)'}}>
                          <FileText className="w-2.5 h-2.5" /> {n}
                        </span>
                      ))}
                    </div>
                  )}
                  {msg.role === 'assistant' ? (
                    <div className="markdown-content" style={{color:'var(--text-primary)'}}>
                      <ReactMarkdown>{msg.content}</ReactMarkdown>
                    </div>
                  ) : (
                    <span style={{color:'var(--text-primary)'}}>{msg.content}</span>
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="rounded-[12px] px-3 py-2 text-[13px]" style={{background:'var(--bg-tertiary)', border:'1px solid var(--border-color)'}}>
                  <div className="flex gap-1">
                    <span className="w-2 h-2 rounded-full bg-[#C9A227] animate-bounce" style={{animationDelay:'0ms'}}></span>
                    <span className="w-2 h-2 rounded-full bg-[#C9A227] animate-bounce" style={{animationDelay:'150ms'}}></span>
                    <span className="w-2 h-2 rounded-full bg-[#C9A227] animate-bounce" style={{animationDelay:'300ms'}}></span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Pièces jointes en attente d'envoi */}
          {pendingDocs.length > 0 && (
            <div className="px-4 pt-2 flex flex-wrap gap-1.5">
              {pendingDocs.map(d => (
                <div key={d.document_id} className="flex items-center gap-1.5 px-2 py-1 rounded-[6px] text-[11px]" style={{background:'var(--bg-tertiary)', border:'1px solid var(--border-color)', color:'var(--text-primary)'}}>
                  {d.is_image ? <ImageIcon className="w-3 h-3 shrink-0" /> : <FileText className="w-3 h-3 shrink-0" />}
                  <span className="max-w-[150px] truncate">{d.filename}</span>
                  <button onClick={() => removePendingDoc(d.document_id)} aria-label={`Retirer ${d.filename}`} className="text-red-400 hover:text-red-300">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Input */}
          <div className="px-4 py-3 border-t" style={{borderColor:'var(--border-color)'}}>
            <div className="flex gap-2">
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".pdf,.docx,.txt,.md,.csv,.png,.jpg,.jpeg,.webp,.heic"
                className="hidden"
                onChange={(e) => { handleFileUpload(e.target.files); e.target.value = ''; }}
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                title="Joindre un document (PDF, DOCX, image...)"
                className="w-9 h-9 rounded-[10px] flex items-center justify-center shrink-0 transition-colors hover:bg-[var(--border-color)] disabled:opacity-40"
                style={{border:'1px solid var(--border-color)'}}
                data-testid="ai-widget-upload-button"
              >
                {uploading
                  ? <Loader2 className="w-4 h-4 animate-spin" style={{color:'#C9A227'}} />
                  : <Paperclip className="w-4 h-4" style={{color:'var(--text-tertiary)'}} />}
              </button>
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                placeholder="Posez votre question..."
                className="flex-1 min-w-0 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-[10px] px-3 py-2 text-[13px]  placeholder:text-[var(--text-tertiary)] outline-none focus:border-[rgba(201,162,39,0.4)]"
                data-testid="ai-widget-message-input"
              />
              <button
                onClick={handleSend}
                disabled={loading || (!input.trim() && pendingDocs.length === 0)}
                className="w-9 h-9 rounded-[10px] flex items-center justify-center shrink-0 transition-colors disabled:opacity-40"
                style={{background:'#C9A227'}}
                data-testid="ai-widget-send-button"
              >
                <Send className="w-4 h-4 text-[#0B1121]" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
